import chroma from 'chroma-js';

export function isColorLight(hex: string): boolean {
  // Remove "#" if present
  hex = hex.replace('#', '');

  // Convert to RGB
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Calculate relative luminance (per W3C)
  const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b);

  // Adjust threshold – lower = more aggressive detection
  return luminance > 150;
}

export function transformColorWithHSL(
  color: string,
  luminanceTarget = 1.0,
  intensity = 1.0
): string {
  // Validate input color
  if (!color || typeof color !== 'string') {
    console.warn('Invalid color input:', color);
    console.trace('invalid color trace');
    return '#dedede'; // Default neutral color
  }

  // Normalize color input (remove whitespace, add # if hex without it)
  color = color.trim().replace(/^(?!#)/, '#');

  try {
    // Validate color format
    if (!/^#?([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(color)) {
      throw new Error('Invalid color format');
    }

    const colorObj = chroma(color);
    const [h, s, l] = colorObj.hsl();

    // For gray colors (h is null/NaN), use the same luminance interpolation formula
    // as colored nodes to ensure consistent behavior
    if (h === null || isNaN(h)) {
      // Use the same formula as colored nodes: interpolate lightness towards target
      const newL = l + (luminanceTarget - l) * (1.0 - intensity);
      // For grays, saturation is 0, so we can create HSL with h=0 (or any value)
      return chroma.hsl(0, 0, newL).hex();
    }

    const newL = l + (luminanceTarget - l) * (1.0 - intensity);

    return chroma.hsl(h, s, newL).hex();
  } catch (e) {
    console.error('Error transforming color:', e, 'Input color:', color);
    return '#dedede'; // Return a neutral color as fallback
  }
}

export function darkenWithHSL(color: string, amount = 0.5): string {
  try {
    const colorObj = chroma(color);
    const [h, s, l] = colorObj.hsl();

    if (h === null || isNaN(h)) {
      return colorObj.darken(amount).hex();
    }

    const newL = Math.max(0, l * (1 - amount));

    return chroma.hsl(h, s, newL).hex();
  } catch (e) {
    console.error('Error darkening color:', e);
    return color; // Return original color as fallback
  }
}

export function brightenWithHSL(color: string, amount = 0.5): string {
  try {
    const colorObj = chroma(color);
    const [h, s, l] = colorObj.hsl();

    if (h === null || isNaN(h)) {
      return colorObj.brighten(amount).hex();
    }

    const newL = l + (1 - l) * amount;

    return chroma.hsl(h, s, newL).hex();
  } catch (e) {
    console.error('Error brightening color:', e);
    return color;
  }
}

export function getColorBrightness(color: string): number {
  try {
    const colorObj = chroma(color);
    return colorObj.luminance() * 100;
  } catch (e) {
    console.error('Error calculating color brightness:', e);
    return 50; // Return middle brightness as fallback
  }
}

// Matches the grid-intensity slider's [min, max] in settings-dialog.component.ts.
const GRID_INTENSITY_MIN = 0.1;
const GRID_INTENSITY_MAX = 0.5;

export function getAdaptiveGridColor(backgroundColor: string, colorIntensity: number, gridIntensityDelta = 0.3): string {
  // Handle empty or invalid background color
  if (!backgroundColor || backgroundColor.trim() === '') {
    return '#dedede';
  }

  try {
    // Calculate the grid intensity based on the background intensity
    let gridIntensity: number;
    if (colorIntensity < 0.5) {
      // Dark background: reflect the delta around the slider's range so a higher
      // grid intensity setting moves the color further from the background (lighter,
      // easier to distinguish from black), while a lower one keeps it closer to the
      // background (darker) - the same direction the light-background branch below uses.
      gridIntensity = Math.min(1.0, colorIntensity + (GRID_INTENSITY_MIN + GRID_INTENSITY_MAX - gridIntensityDelta));
    } else {
      // Subtract delta from intensity for lighter grid color
      gridIntensity = Math.max(0.0, colorIntensity - gridIntensityDelta);
    }

    // Apply the same color transformation as the background but with the adjusted intensity
    return transformColorWithHSL(backgroundColor, 1.0, gridIntensity);
  } catch (e) {
    console.error('Error calculating adaptive grid color:', e, 'Input color:', backgroundColor);
    return '#dedede'; // Return default grid color as fallback
  }
}
